import { useCallback, useMemo, useState } from 'react';
import { useCurrentUser } from 'stores/useUserStore';
import styled from 'styled-components';

import LoggingService from 'components/core/logging/LoggingService';
import Image from 'components/ui/images/Images';
import MenuButton, { MenuItem } from 'components/ui/menus/MenuButton';
import { ImageSize, ImageType } from 'enums/imageType';
import { RoutePath } from 'enums/routePath';
import { ElementTestId } from 'enums/testing';
import { useRouter } from 'hooks/useRouter';
import { authManager, isLoggedIn } from 'utils/authUtils';
import { translate } from 'utils/intlUtils';

import { HEIGHT_750, WIDTH_750 } from 'styles/tokens';

import { NavActionItemBox } from '../shared/CommonNavItems';
import ImpersonationToggle from './ImpersonationToggle';
import LanguageToggle from './LanguageToggle';

const { t } = translate;

const UserNavActionItem = styled(NavActionItemBox)`
  padding: 0;
`;

const UserImage = styled(Image)`
  height: ${HEIGHT_750};
  width: ${WIDTH_750};
`;

const UserMenuNavItem = () => {
  const { avatar } = useCurrentUser();

  const isAuthenticated = isLoggedIn();
  const router = useRouter();
  const logout = useCallback(async () => {
    await authManager.logout();
    LoggingService.clearUser();
  }, []);

  const customIcon = useMemo(
    () => (
      <UserNavActionItem data-testid={ElementTestId.USER_MENU_GLOBAL_NAV_BUTTON}>
        <UserImage size={ImageSize.AVATAR} src={avatar?.url} type={ImageType.AVATAR} />
      </UserNavActionItem>
    ),
    [avatar?.url]
  );

  const [isLanguageToggleOpen, setIsLanguageToggleOpen] = useState<boolean>(false);

  const toggleLanguage = useCallback(isOpen => () => setIsLanguageToggleOpen(isOpen), [setIsLanguageToggleOpen]);

  return (
    <MenuButton ariaLabel={t('user_menu')} customIcon={customIcon} position="bottom-left" type="custom">
      {isAuthenticated ? (
        <>
          <MenuItem label={t('language_one')} onClick={toggleLanguage(true)} />
          <MenuItem
            label={t('terms_and_privacy_policy')}
            onClick={() => window.open(process.env.REACT_APP_PRIVACY_POLICY_URL, '_blank')}
          />
          <MenuItem label={t('my_account')} onClick={() => router.push(RoutePath.MY_ACCOUNT)} />
          <ImpersonationToggle />
          <MenuItem label={t('logout')} onClick={() => void logout()} testId={ElementTestId.LOGOUT_USER_MENU_ITEM} />

          <LanguageToggle isOpen={isLanguageToggleOpen} onCancel={toggleLanguage(false)} />
        </>
      ) : null}
    </MenuButton>
  );
};

export default UserMenuNavItem;
